/**
 * Currency display helpers (issue #140).
 *
 * MedusaJS exposes a region's `currency_code`; its guidance for *display* is
 * the platform `Intl.NumberFormat` API (no extra dependency). The locale
 * switcher shows the currency symbol derived from the catalog currency code,
 * so the UI is data-driven from the region rather than hardcoded in
 * translation strings. (Price formatting itself lands with the Medusa region
 * wiring in PR #6 — this is the switcher's symbol only.)
 */

/**
 * Resolve the narrow currency symbol for `currencyCode` as rendered in
 * `locale` (e.g. SEK+sv-SE → "kr", USD+en-US → "$"). Falls back to the ISO
 * code if Intl can't resolve it (older ICU / unknown currency).
 */
export function currencySymbol(currencyCode: string, locale: string): string {
	if (!currencyCode) return "";
	try {
		const parts = new Intl.NumberFormat(locale, {
			style: "currency",
			currency: currencyCode,
			currencyDisplay: "narrowSymbol",
		}).formatToParts(0);
		return parts.find((part) => part.type === "currency")?.value ?? currencyCode;
	} catch {
		return currencyCode;
	}
}

/**
 * Format an amount in `currencyCode` for `locale` via Intl.NumberFormat.
 * Provided for the switcher + future price rendering; safe fallback on error.
 */
export function formatCurrency(amount: number, currencyCode: string, locale: string): string {
	try {
		return new Intl.NumberFormat(locale, {
			style: "currency",
			currency: currencyCode,
		}).format(amount);
	} catch {
		return `${amount} ${currencyCode}`;
	}
}
